import { Injectable } from '@angular/core';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

/**
 * Phantom auth probe
 *
 * "Phantom auth" = the app keeps running with a cached Firebase user reference
 * but the underlying credentials are dead, so every Firestore read/write fails
 * with permission-denied. The classic trigger is the user clearing site data
 * via DevTools — IndexedDB is wiped externally, in-memory state survives, and
 * no normal logout flow runs.
 *
 * Build doesn't route Firestore calls through a single wrapper, so we can't
 * intercept errors centrally. Instead we probe periodically: read a doc the
 * authenticated user must be able to read (their own user doc) and check
 * whether it comes back permission-denied while we still think we're signed
 * in. If yes, reload — Firebase Auth re-reads (empty) IndexedDB on boot and
 * the user is signed out cleanly.
 */
@Injectable({ providedIn: 'root' })
export class PhantomAuthService {
  private readonly PHANTOM_AUTH_PROBE_INTERVAL_MS = 30_000;
  private recoveryTriggered = false;
  private probeStarted = false;

  public start(): void {
    if (this.probeStarted || typeof window === 'undefined') return;
    this.probeStarted = true;
    setInterval(() => this.probe(), this.PHANTOM_AUTH_PROBE_INTERVAL_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.probe();
    });
  }

  private async probe(): Promise<void> {
    if (this.recoveryTriggered) return;
    const user = getAuth().currentUser;
    if (!user) return;
    try {
      await getDoc(doc(getFirestore(), 'users', user.uid));
    } catch (error: any) {
      if (error?.code !== 'permission-denied') return;
      this.recoveryTriggered = true;
      console.warn('[PhantomAuth] permission-denied on own user doc while currentUser is set — reloading to recover');
      window.location.reload();
    }
  }
}
